"use client";

import { useState, useRef, useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/routing";
import { type Locale } from "@/i18n/config";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/track";

const FLAG_EMOJIS: Record<string, string> = {
  en: "🇺🇸",
  he: "🇮🇱",
  ar: "🇸🇦",
  fr: "🇫🇷",
  es: "🇪🇸",
  de: "🇩🇪",
  zh: "🇨🇳",
  ja: "🇯🇵",
  ko: "🇰🇷",
  ru: "🇷🇺",
  pt: "🇧🇷",
  it: "🇮🇹",
  tr: "🇹🇷",
  nl: "🇳🇱",
  pl: "🇵🇱",
};

interface ActiveLanguage {
  code: string;
  nativeName: string;
}

export default function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("language");
  const [isOpen, setIsOpen] = useState(false);
  const [languages, setLanguages] = useState<ActiveLanguage[]>([]);
  // 'down' = drop below the toggle (default in Header). 'up' = above
  // (when toggle is near the viewport bottom — e.g. inside MobileNav
  // footer). Computed on every open so rotating the device picks
  // the right side. (#160 — list was clipped on mobile.)
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch active languages from DB
  useEffect(() => {
    fetch("/api/languages")
      .then((r) => r.json())
      .then((data) => {
        const active = (Array.isArray(data) ? data : [])
          .filter((l: { isActive: boolean }) => l.isActive)
          .map((l: { code: string; nativeName: string }) => ({ code: l.code, nativeName: l.nativeName }));
        setLanguages(active.length > 0 ? active : [{ code: "en", nativeName: "English" }]);
      })
      .catch(() => setLanguages([{ code: "en", nativeName: "English" }]));
  }, []);

  const switchLocale = (newLocale: Locale) => {
    trackEvent("language_switched", { from: locale, to: newLocale });
    if (newLocale === "en") {
      // Delete the locale cookie for English (default locale doesn't need one)
      document.cookie = "NEXT_LOCALE=;path=/;max-age=0;samesite=lax";
    } else {
      document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${365 * 24 * 60 * 60};samesite=lax`;
    }
    router.replace(pathname, { locale: newLocale });
    setIsOpen(false);
  };

  // Don't show switcher if only one language is active
  if (languages.length <= 1) return null;

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          // Decide drop direction based on available space below
          // the toggle. Flip up when the dropdown wouldn't fit
          // beneath without spilling past the viewport.
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownEstimate = Math.min(window.innerHeight * 0.7, 600);
            setDropDirection(spaceBelow < dropdownEstimate ? "up" : "down");
          }
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-1.5 p-1.5 text-brand-dark/70 hover:text-brand-gold transition-colors"
        aria-label={t("selectLanguage")}
      >
        <Globe className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider hidden sm:inline">
          {locale}
        </span>
      </button>

      {isOpen && (
        // 15 locales × ~40px ≈ 600px — without max-height + overflow
        // the dropdown was getting clipped by the mobile nav drawer's
        // bottom edge, leaving customers stuck on the first 4-5
        // languages (#160). max-h-[70vh] + overflow-y-auto lets them
        // scroll the list independently of the drawer.
        // Direction flips when there's no room below the toggle.
        <div
          className={cn(
            "absolute right-0 z-50 w-48 bg-white border border-gray-100 rounded-lg shadow-xl overflow-y-auto overscroll-contain max-h-[70vh]",
            dropDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"
          )}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => switchLocale(lang.code as Locale)}
              className={cn(
                "flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-brand-cream/50 transition-colors text-start",
                lang.code === locale
                  ? "text-brand-gold font-medium bg-brand-cream/30"
                  : "text-brand-dark/70"
              )}
            >
              <span className="text-base">{FLAG_EMOJIS[lang.code] || "🌐"}</span>
              <span>{lang.nativeName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
